import { Fragment, ReactNode } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogPanel,
  Transition,
  TransitionChild
} from '@headlessui/react'

import { CloseButton } from '@/components/CloseButton'

export type ModalProps = {
  open: boolean
  onClose: () => void
  title: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  buttons?: ReactNode
  children: ReactNode
}

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  buttons,
  children
}: ModalProps) {
  const widthClass =
    size === 'sm'
      ? 'max-w-md'
      : size === 'md'
        ? 'max-w-lg'
        : size === 'lg'
          ? 'max-w-2xl'
          : size === 'xl'
            ? 'max-w-screen-xl'
            : ''

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 overflow-y-auto"
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center overflow-y-auto py-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={`relative w-full ${widthClass} transform overflow-hidden rounded-lg bg-gray-700 shadow-xl transition-all`}
              >
                <div className="absolute right-0 top-0 bg-gray-700 pr-6 pt-5 sm:block">
                  <CloseButton onClick={onClose} />
                </div>
                <DialogTitle
                  as="h3"
                  className="my-5 bg-gray-700 text-base font-medium leading-6 text-white"
                >
                  {title}
                </DialogTitle>
                <div className="w-full border-t border-gray-300" />
                <div
                  style={{ maxHeight: '70vh' }}
                  className="overflow-y-auto bg-white"
                >
                  {children}
                </div>
                {buttons && (
                  <div className="bg-white">
                    <div className="w-full border-t border-gray-300" />
                    <div className="px-6 py-4">{buttons}</div>
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
